import { useState, useEffect, lazy, Suspense, useRef } from "react";
import "shepherd.js/dist/css/shepherd.css";
import "./walkthrough.css";
import theme from "./theme";
import { ThemeProvider } from "@mui/material/styles";
import { CssBaseline, Grid } from "@mui/material";
import { HashRouter as Router, Route, useLocation } from "react-router-dom";
import posthog from "posthog-js";
//
import Home from "./components/Home";
import Definition from "./components/Definition";
import logger, { LoggerContainer, useLoggerApi } from "logrock";
import { initDictionaryDB } from "./utils/dictionary-db";
import { scheduleDictionaryUpdates } from "./utils/dictionary-update";
import { registerAndroidBackButton } from "./utils/androidBackButton";
import { watchSoftKeyboard } from "./utils/softKeyboard";
import { AppContextProvider } from "./utils/AppContext";
import BottomNav from "./components/BottomNav";
import DesktopSidebar from "./components/DesktopSidebar";
import KeyboardShortcuts from "./components/KeyboardShortcuts";

const Bookmarks    = lazy(() => import("./components/Bookmarks"));
const Flashcards   = lazy(() => import("./components/Flashcards"));
const History      = lazy(() => import("./components/History"));
const PrivacyPolicy = lazy(() => import("./components/PrivacyPolicy/PrivacyPolicy"));
const Settings     = lazy(() => import("./components/Settings"));

const PageViewTracker = () => {
  const location = useLocation();
  useEffect(() => {
    posthog.capture("$pageview", { $current_url: window.location.href });
  }, [location]);
  return null;
};

const ScrollToTop = () => {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  return null;
};

const App = () => {
  useEffect(() => registerAndroidBackButton(), []);
  useEffect(() => watchSoftKeyboard(), []);
  // Over-the-air dictionary data: check after startup and on app resume.
  useEffect(() => scheduleDictionaryUpdates(), []);

  useEffect(() => {
    initDictionaryDB().catch((err) =>
      console.error("Failed to initialize dictionary DB:", err)
    );
    // Prefetch lazy route chunks during browser idle time so first tab-tap is instant
    const prefetch = () => {
      import("./components/Bookmarks");
      import("./components/Flashcards");
      import("./components/History");
      import("./components/PrivacyPolicy/PrivacyPolicy");
      import("./components/Settings");
    };
    const id = window.requestIdleCallback
      ? window.requestIdleCallback(prefetch)
      : setTimeout(prefetch, 2000);
    return () => (window.cancelIdleCallback ? window.cancelIdleCallback(id) : clearTimeout(id));
  }, []);

  return (
    <AppContextProvider>
      <LoggerContainer
        sessionID={window.sessionID}
        active={process.env.REACT_APP_LOCAL === "1"}
      >
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Grid
            container
            sx={{
              pr: { xs: 2, sm: 3 },
              pl: { xs: 2, sm: "calc(var(--hw-sidebar-w) + 24px)" },
              pt: { xs: 0, sm: 2 },
              pb: { xs: "calc(64px + env(safe-area-inset-bottom, 0px))", sm: 2 },
              mt: { xs: 0, sm: 2 },
              transition: "padding-left 0.25s cubic-bezier(0.4,0,0.2,1)",
            }}
            justifyContent="flex-start"
          >
            <Grid item xs={12}>
              <Router>
                <PageViewTracker />
                <ScrollToTop />
                <DesktopSidebar />
                <KeyboardShortcuts />
                <Route exact path="/">
                  <Home />
                </Route>
                <Route path="/search/:word">
                  <Definition />
                </Route>
                <Suspense fallback={null}>
                  <Route path="/bookmarks">
                    <Bookmarks />
                  </Route>
                  <Route path="/flashcards">
                    <Flashcards />
                  </Route>
                  <Route path="/history">
                    <History />
                  </Route>
                  <Route path="/privacy-policy">
                    <PrivacyPolicy />
                  </Route>
                  <Route path="/settings">
                    <Settings />
                  </Route>
                </Suspense>
                <BottomNav />
              </Router>
            </Grid>
          </Grid>
        </ThemeProvider>
      </LoggerContainer>
    </AppContextProvider>
  );
};

export default App;
